// Draws every bug the simulation owns. Reads sim state, never mutates it
// (00-architecture.md §1); the placement math is the surface frame from
// `game/surface.ts` with the cooked model's axis fix applied on top.

import { type CookedModel, ModelRenderer, poseWorlds } from "../model";
import { multiply, scaling, type Mat4 } from "../math";
import type { Vec3 } from "../game/surface";
import { MODEL_CATALOG, MODEL_FIX, type ModelName } from "./model-catalog";
import { bugFrame, type Bug, type BugId, type BugKind } from "../game/bugs";

/** Where a model stands and which way it faces — `poseToWorld`'s result shape. */
export interface SurfaceFrame {
  readonly pos: Vec3;
  readonly normal: Vec3;
  readonly forward: Vec3;
}

// Cooked models use the Unity scale: one block = 3 units; here a cell is 0.2.
// Production renders bugs at ~2x the raw model size (unity-overview.md §4).
export const MODEL_SCALE = (2.0 * 0.2) / 3;

/** Clip name and time for a bug's current state, plus its grow-in scale. */
interface Appearance {
  readonly clip: string;
  readonly time: number;
  readonly loop: boolean;
  /** Uniform scale factor on top of MODEL_SCALE. */
  readonly grow: number;
}

/** Clip duration, or 0 when the model has no such clip (`poseWorlds` rests). */
function duration(model: CookedModel, clip: string): number {
  return model.anims.get(clip)?.duration ?? 0;
}

function appearance(bug: Bug, model: CookedModel): Appearance {
  const state = bug.state;
  switch (state.kind) {
    case "spawning":
      // Grow in from nothing, legs still, so the arrival reads as an arrival.
      return { clip: "run", time: 0, loop: true, grow: state.progress };
    case "running":
      return { clip: "run", time: bug.lifeTime, loop: true, grow: 1 };
    case "infecting":
      // One bite, played once and held: the sim's BITE_SECONDS is the cooked
      // clip's own 0.8 s, but stretching it here keeps the black bug's shorter
      // clip in step too, exactly as `die` does below.
      return {
        clip: "infect",
        time: state.progress * duration(model, "infect"),
        loop: false,
        grow: 1,
      };
    case "dying":
      // Plays once and holds the last frame until the sim removes the bug. The
      // clip is stretched over the sim's DIE_SECONDS: the cooked durations run
      // 0.63 s (black) to 1.8 s (standard, fat), and the sim owns the timing.
      return { clip: "die", time: state.progress * duration(model, "die"), loop: false, grow: 1 };
    case "takenByPlayer": {
      // Only the lady and black bugs can be picked up; the rest fall back to
      // `die`, which every bug has (plan 08 drives this state).
      const clip = model.anims.has("pick") ? "pick" : "die";
      return { clip, time: state.progress * duration(model, clip), loop: false, grow: 1 };
    }
    default: {
      const unreachable: never = state;
      throw new Error(`unhandled bug state ${JSON.stringify(unreachable)}`);
    }
  }
}

/**
 * Cube-local placement of a roster model standing on the surface at `frame`: an
 * orthonormal basis from the surface frame, the catalog's axis fix, and a lift
 * that puts the model's lowest point on the rounded surface. `grow` is the
 * spawn-in scale (1 once grown). Shared with the props `main.ts` places.
 *
 * It takes a frame rather than a pose because a turning bug follows an arc: its
 * heading between the two quarter-turn directions has no `SurfacePose` to
 * describe it (`bugFrame` in `game/bugs.ts`).
 */
export function placeOnSurface(frame: SurfaceFrame, name: ModelName, grow: number): Mat4 {
  const entry = MODEL_CATALOG[name];
  const { pos, normal: up, forward } = frame;
  const right = [
    up[1] * forward[2] - up[2] * forward[1],
    up[2] * forward[0] - up[0] * forward[2],
    up[0] * forward[1] - up[1] * forward[0],
  ];
  // `forward` is finite-differenced off the rounded surface, so it is only
  // near-tangent; rebuilding it from right x up makes the basis exactly
  // orthonormal without changing the visible heading.
  const fwd = [
    right[1] * up[2] - right[2] * up[1],
    right[2] * up[0] - right[0] * up[2],
    right[0] * up[1] - right[1] * up[0],
  ];
  const m = new Float32Array(16);
  m[0] = right[0];
  m[1] = right[1];
  m[2] = right[2];
  m[4] = up[0];
  m[5] = up[1];
  m[6] = up[2];
  m[8] = fwd[0];
  m[9] = fwd[1];
  m[10] = fwd[2];
  const lift = entry.lift * MODEL_SCALE * grow;
  m[12] = pos[0] + up[0] * lift;
  m[13] = pos[1] + up[1] * lift;
  m[14] = pos[2] + up[2] * lift;
  m[15] = 1;
  return multiply(multiply(m, entry.fix ?? MODEL_FIX), scaling(MODEL_SCALE * grow));
}

export class BugRenderer {
  constructor(private readonly models: Record<BugKind, CookedModel>) {}

  draw(
    gl: WebGL2RenderingContext,
    renderer: ModelRenderer,
    bugs: ReadonlyMap<BugId, Bug>,
    cubeModel: Mat4,
    viewProj: Mat4,
  ): void {
    for (const bug of bugs.values()) {
      const model = this.models[bug.kindDef];
      const look = appearance(bug, model);
      if (look.grow <= 0) continue; // a zero-scale model is invisible anyway
      const worlds = poseWorlds(model, look.clip, look.time, look.loop);
      renderer.draw(
        gl,
        model,
        worlds,
        multiply(cubeModel, placeOnSurface(bugFrame(bug), bug.kindDef, look.grow)),
        viewProj,
      );
    }
  }
}
